import { InputAdornment, MenuItem, TextField } from '@mui/material';
import { fmtMoney } from '@/lib/money';
import { textFieldProps } from '../textFieldProps';
import type { FieldComponentProps, FieldTypeSpec } from '../types';

export function TextInput({ field, value, onChange, onBlur, error }: FieldComponentProps<string | null>) {
  return <TextField {...textFieldProps(field, error)} value={value ?? ''} onBlur={onBlur}
                    onChange={(e) => onChange(e.target.value)} />;
}

export interface TextareaConfig { rows?: number; maxRows?: number }

export function TextareaInput({ field, value, onChange, onBlur, error }: FieldComponentProps<string | null, TextareaConfig>) {
  return <TextField {...textFieldProps(field, error)} multiline minRows={field.config.rows ?? 3}
                    maxRows={field.config.maxRows} value={value ?? ''} onBlur={onBlur}
                    onChange={(e) => onChange(e.target.value)} />;
}

export interface NumberConfig { step?: number; prefix?: string; suffix?: string; decimals?: number; symbol?: string }

function toNumber(text: string): number | null {
  if (text.trim() === '') return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

/** Numbers, money and percentages share one input; the type only changes the adornments. */
export function NumberInput({ field, value, onChange, onBlur, error }: FieldComponentProps<number | null, NumberConfig>) {
  const { prefix, suffix, step } = field.config;
  const base = textFieldProps(field, error);
  return (
    <TextField {...base} type="number" value={value ?? ''} onBlur={onBlur}
               onChange={(e) => onChange(toNumber(e.target.value))}
               slotProps={{
                 ...base.slotProps,
                 htmlInput: { step: step ?? 'any', ...(base.slotProps?.htmlInput as object) },
                 input: {
                   ...(prefix ? { startAdornment: <InputAdornment position="start">{prefix}</InputAdornment> } : {}),
                   ...(suffix ? { endAdornment: <InputAdornment position="end">{suffix}</InputAdornment> } : {}),
                   ...(base.slotProps?.input as object),
                 },
               }} />
  );
}

export function MoneyInput(props: FieldComponentProps<number | null, NumberConfig>) {
  const config = { step: 0.01, prefix: props.field.config.symbol ?? '₹', ...props.field.config };
  return <NumberInput {...props} field={{ ...props.field, config }} />;
}

export function PercentInput(props: FieldComponentProps<number | null, NumberConfig>) {
  return <NumberInput {...props} field={{ ...props.field, config: { suffix: '%', ...props.field.config } }} />;
}

const UNITS = { KB: 1024, MB: 1024 ** 2, GB: 1024 ** 3, TB: 1024 ** 4 } as const;
type Unit = keyof typeof UNITS;

export interface BytesConfig { units?: Unit[]; unlimitedLabel?: string }

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const unit = (['TB', 'GB', 'MB', 'KB'] as Unit[]).find((u) => bytes >= UNITS[u]) ?? 'KB';
  return `${Number((bytes / UNITS[unit]).toFixed(1))} ${unit}`;
}

function bestUnit(bytes: number | null, units: Unit[]): Unit {
  const fallback = units.includes('MB') ? 'MB' : units[0]!;
  if (!bytes) return fallback;
  return [...units].reverse().find((u) => bytes % UNITS[u] === 0) ?? fallback;
}

/** A size held in bytes, entered in a unit; empty means "no limit" when the field allows it. */
export function BytesInput({ field, value, onChange, onBlur, error }: FieldComponentProps<number | null, BytesConfig>) {
  const units = field.config.units ?? ['MB', 'GB'];
  const unit = bestUnit(value, units);
  const base = textFieldProps(field, error);
  const shown = value == null ? '' : String(Number((value / UNITS[unit]).toFixed(3)));
  return (
    <TextField {...base} type="number" value={shown} onBlur={onBlur}
               placeholder={field.placeholder ?? field.config.unlimitedLabel}
               onChange={(e) => {
                 const n = toNumber(e.target.value);
                 onChange(n == null ? null : Math.round(n * UNITS[unit]));
               }}
               slotProps={{
                 ...base.slotProps,
                 htmlInput: { min: 0, step: 'any', ...(base.slotProps?.htmlInput as object) },
                 input: {
                   endAdornment: (
                     <TextField select variant="standard" value={unit} disabled={field.disabled}
                                slotProps={{ input: { disableUnderline: true } }}
                                onChange={(e) => value != null &&
                                  onChange(Math.round((value / UNITS[unit]) * UNITS[e.target.value as Unit]))}>
                       {units.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                     </TextField>
                   ),
                 },
               }} />
  );
}

export const textTypes: Record<string, FieldTypeSpec> = {
  text: { component: TextInput },
  textarea: { component: TextareaInput, defaults: { grid: { xs: 12, md: 12 } } },
  number: { component: NumberInput, check: (v) => (typeof v === 'number' ? undefined : 'Enter a number.') },
  money: {
    component: MoneyInput,
    display: (v, f) => {
      const c = f.config as NumberConfig;
      return fmtMoney(v as number, c.symbol ?? '₹', c.decimals == null ? undefined : { precision: c.decimals });
    },
  },
  percent: { component: PercentInput, display: (v) => `${v}%` },
  bytes: { component: BytesInput, display: (v) => formatBytes(v as number) },
};
